import type { ArtifactCreationBody, ArtifactSimpleResource } from '@ingenico/react-form-builder';
import { ExposedDataFileAutocomplete, ExposedUploadFileModal } from '@ingenico/react-form-builder';
import type { RenderSmartFieldDataFileInputParams } from '@ingenico/react-smartfield-utils/smartfield-ui';
import { KebabMenu } from '@ingenico/react-ui/kebab-menu';
import type { ReactElement } from 'react';
import styles from './ParameterModuleSmartFieldDataFileInput.module.scss';
import { useParameterModuleSmartFieldDataFileInput } from './useParameterModuleSmartFieldDataFileInput.hook';

interface ParameterModuleSmartFieldDataFileInputProps
  extends Pick<RenderSmartFieldDataFileInputParams, 'config' | 'fieldState' | 'onChange' | 'value'> {
  readonly artifacts: ArtifactSimpleResource[];
  readonly artifactsIsLoading: boolean;
  readonly onArtifactSearch: (query: string, acceptedExtensions?: readonly string[]) => void;
  readonly onArtifactCreate: (
    payload: ArtifactCreationBody,
    onSuccess?: (artifact: ArtifactSimpleResource) => void,
    onError?: () => void,
  ) => void;
  readonly onArtifactPreview: (id: string) => void;
}

export function ParameterModuleSmartFieldDataFileInput({
  config,
  value,
  onChange,
  fieldState,
  artifacts,
  artifactsIsLoading,
  onArtifactSearch,
  onArtifactCreate,
  onArtifactPreview,
}: ParameterModuleSmartFieldDataFileInputProps): ReactElement {
  const vm = useParameterModuleSmartFieldDataFileInput({
    config,
    artifacts,
    artifactsIsLoading,
    onArtifactSearch,
    onArtifactCreate,
    onArtifactPreview,
  });

  return (
    <div className={styles.dataFileRow}>
      <ExposedDataFileAutocomplete
        label={vm.label}
        name="smartFieldDataFileInput"
        value={value}
        artifacts={vm.artifacts}
        isLoading={vm.artifactsIsLoading}
        acceptedExtensions={config.inputCustomConfig.acceptedExtensions}
        onSearch={(query) => vm.onArtifactSearch(query, config.inputCustomConfig.acceptedExtensions)}
        onChange={onChange}
        isEditable
        required={vm.isRequired}
        error={fieldState.invalid}
        helperText={fieldState.error?.message}
      />
      <KebabMenu options={vm.getKebabOptions(value, onChange)} shouldAddDisablePortal />
      <ExposedUploadFileModal
        isOpen={vm.isUploadModalOpen}
        isLoading={vm.isUploadLoading}
        isFormValid={vm.isUploadFormValid}
        control={vm.uploadControl}
        fileRules={vm.fileRules}
        onClose={vm.onUploadModalClose}
        onChangeFiles={vm.onChangeUploadFiles}
        onUpload={() => vm.onUpload(onChange)}
        fileToUpload={vm.fileToUpload}
        t={vm.t}
      />
    </div>
  );
}
